<template>
    <div data-test-view="reviews-cycles">
        <l-card-content>
            <l-headline :title="$t(`reviews.list.titleAdminCycle`)" :tabs="tabs" />

            <l-space :size="1" />

            <!-- Filters -->
            <div class="row">
                <div class="col-xs-4 title-input">
                    <l-select
                        v-model="reviewFilter"
                        :options="optionsReviewFilter"
                        @change="e => getReviewCycleListByCycle(e.target.value)"
                    />
                </div>

                <div v-if="reviewFilter === 'specificOwner'" class="col-xs-8 hidden-xs title-input">
                    <l-input-typeahead
                        :label="``"
                        :placeholder="$t(`reviews.list.placeholderOwner`)"
                        :data="users"
                        :on-select="typeaheadOnSelect"
                        :on-input-change="typeaheadOnInputChange"
                        remove-margin
                        display-selected-value
                    />
                </div>
                <div v-else-if="reviewFilter === 'specificAutomation'" class="col-xs-8 title-input">
                    <l-select
                        v-model="reviewFilterSpecificAutomation"
                        :options="optionsFilterSpecificAutomation"
                        @change="
                            e => getReviewCycleListByCycle('specificAutomation', e.target.value)
                        "
                    />
                </div>

                <div v-else class="col-xs-8 title-input">
                    <l-input-text
                        v-model="reviewFilterValue"
                        type="text"
                        :label="``"
                        :placeholder="$t(`reviews.list.placeholderSearch`)"
                        hide-help-block
                        optional
                        data-test-id="review-filter-value"
                        @input="search"
                    />
                </div>
            </div>
            <!--/Filters -->

            <l-space :size="1" />

            <!-- List -->
            <l-no-data
                v-show="!reviewCycleList.admin.length"
                :text="$t(`reviews.list.adminEmptyH1`)"
                data-test-id="reviews-cycles-no-data"
            />

            <div v-for="(reviewCycle, index) in orderedReviewCycleListAdmin" :key="reviewCycle._id">
                <hr v-show="index !== 0" />

                <l-reviews-cycles-list-item :review-cycle="reviewCycle" />
            </div>
            <!--/List -->
        </l-card-content>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import debounce from "lodash.debounce";

import format from "@/utils/format-typeahead";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LNoData from "@/components/LNoData";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";

import LReviewsCyclesListItem from "@/components/LReviewsCyclesListItem";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputText,
        LInputTypeahead,
        LNoData,
        LSelect,
        LSpace,

        LReviewsCyclesListItem,
    },
    data() {
        return {
            reviewFilter: "default",
            reviewFilterValue: "",
            reviewFilterSpecificAutomation: "",
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            usersSearchList: "users/search/list",
            reviewAutomationList: "reviews/automations/list",
            reviewCycleList: "reviews/assessments/list",
        }),

        tabs() {
            return [
                {
                    show: true,
                    label: this.$t(`reviews.list.labelActiveCycles`),
                    tabClass: "descriptive",
                },
                {
                    show: true,
                    tabClass: "space",
                },
                {
                    label: this.$t(`reviews.list.btnChooseTemplate`),
                    handler: () => this.createCycle(),
                    show: true,
                    isButton: true,
                    tabClass: "btn-primary",
                },
            ];
        },

        users() {
            return this.usersSearchList.map(format.users);
        },

        orderedReviewCycleListAdmin() {
            return Array.from(this.reviewCycleList.admin).sort((a, b) => {
                if (a.type < b.type) {
                    return 1;
                }
                if (a.type > b.type) {
                    return -1;
                }
                if (new Date(a.deadline) < new Date(b.deadline)) {
                    return 1;
                }
                if (new Date(a.deadline) > new Date(b.deadline)) {
                    return -1;
                }
                return 0;
            });
        },

        optionsReviewFilter() {
            const options = [];

            options.push(
                ...[
                    {
                        value: "default",
                        text: this.$t(`reviews.list.optionDefault`),
                    },
                    {
                        value: "defaultAutomation",
                        text: this.$t(`reviews.list.optionDefaultAutomation`),
                    },
                    {
                        value: "me",
                        text: this.$t(`reviews.list.optionMe`),
                    },
                ],
            );

            if (this.user.rights.R_ACCESS_ALL) {
                options.push({
                    value: "others",
                    text: this.$t(`reviews.list.optionOthers`),
                });
                options.push({
                    value: "specificOwner",
                    text: this.$t(`reviews.list.optionSpecificOwner`),
                });
                options.push({
                    value: "specificAutomation",
                    text: this.$t(`reviews.list.optionSpecificAutomation`),
                });
                options.push({
                    value: "all",
                    text: this.$t(`reviews.list.optionAll`),
                });
            }

            options.push(
                ...[
                    {
                        value: "draft",
                        text: this.$t(`reviews.list.optionDraft`),
                    },
                    {
                        value: "upcoming",
                        text: this.$t(`reviews.list.optionUpcoming`),
                    },
                    {
                        value: "ongoing",
                        text: this.$t(`reviews.list.optionOngoing`),
                    },
                    {
                        value: "complete",
                        text: this.$t(`reviews.list.optionComplete`),
                    },
                ],
            );

            return options;
        },

        optionsFilterSpecificAutomation() {
            return [
                {
                    value: "",
                    text: this.$t(`reviews.list.placeholderAutomation`),
                },
                ...this.reviewAutomationList.map(automation => ({
                    value: automation._id,
                    text: automation.name,
                })),
            ];
        },
    },
    methods: {
        async getReviewCycleListByCycle(filter, filterValue) {
            if (
                !filter ||
                (filter === "specificOwner" && !filterValue) ||
                (filter === "specificAutomation" && !filterValue)
            )
                return;

            // set filter to 'all' if searching

            if (filterValue && filter === "all") this.reviewFilter = "all";

            if (filter !== "all") this.reviewFilterValue = "";

            if (filter !== "specificAutomation") this.reviewFilterSpecificAutomation = "";

            if (filter !== "specificOwner") this.reviewFilterSpecificOwner = "";

            await this.$store.dispatch({
                type: "reviews/assessments/getList",
                payload: { scope: "admin", filter, filterValue, offset: 0 },
            });
        },
        createCycle() {
            this.$router.push(`/reviews/templates`);
        },
        typeaheadOnSelect(user) {
            this.getReviewCycleListByCycle("specificOwner", user._id);

            this.$store.dispatch("users/search/reset");
        },
        typeaheadOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query, sort: "default" });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },

        search: debounce(function() {
            this.getReviewCycleListByCycle("all", this.reviewFilterValue || "none");
        }, 500),

        reviewCycleCreateUpdate(opts) {
            this.$router.push(`/reviews/create/${opts.reviewCycleId}/${opts.entrypoint}`);
        },
    },
};
</script>
